import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import { Gift, Sparkles, Clock } from "lucide-react";
import { api } from "../../lib/api";
import { useCart } from "../../lib/cart";
import { loadSpinSession, saveSpinSession, clearSpinSession, setPendingReward, fmtExpiry } from "../../lib/spin";
import SpinWheel, { segmentsOf } from "./SpinWheel";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";

const err = (e, f) => e.response?.data?.detail || f;

function VerifyBox({ onVerified }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/spin/request-code", { email }); setSent(true); toast.success("Code sent, check your email"); }
    catch (e) { toast.error(err(e, "Could not send code")); } finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { const r = await api.post("/spin/verify", { email, code }); saveSpinSession(r.data); onVerified(r.data); toast.success("Email verified!"); }
    catch (e) { toast.error(err(e, "Verification failed")); } finally { setBusy(false); }
  };
  return (
    <div className="w-full max-w-sm space-y-2" data-testid="spin-verify">
      <p className="text-white/70 text-sm">Verify your email to spin. One spin per customer every few days.</p>
      <div className="flex gap-2">
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="bg-white" data-testid="spin-email-input" disabled={sent} />
        {!sent && <Button onClick={send} disabled={busy || !email} className="mlobox-gradient text-white rounded-full" data-testid="spin-send-code">Get code</Button>}
      </div>
      {sent && (
        <div className="flex gap-2">
          <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code" inputMode="numeric" className="bg-white tracking-widest" data-testid="spin-code-input" />
          <Button onClick={verify} disabled={busy || code.length !== 6} className="mlobox-gradient text-white rounded-full" data-testid="spin-verify-btn">Verify</Button>
        </div>
      )}
    </div>
  );
}

function ResultDialog({ result, onClose }) {
  const navigate = useNavigate();
  const { count } = useCart();
  const rw = result?.reward;
  const use = () => {
    setPendingReward(rw.code); onClose();
    if (count) navigate("/checkout"); else toast.info("Reward saved! Add a meal to your cart, then use it at checkout.");
  };
  return (
    <Dialog open={!!result} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-sm w-[calc(100%-2rem)] text-center" data-testid="spin-result-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-center">{rw ? "You won!" : "So close!"}</DialogTitle>
          <DialogDescription className="text-center">{rw ? "Your reward is saved to your email." : "Better luck next time. Come back for your next spin."}</DialogDescription>
        </DialogHeader>
        <div className="text-3xl font-extrabold text-[#CC1A00] font-display" data-testid="spin-result-label">{result?.label}</div>
        {rw && (
          <div className="rounded-2xl border-2 border-dashed border-[#FF6600] bg-[#FBF6EE] p-4 space-y-1 text-sm">
            <div>Reward code: <b className="tracking-wider" data-testid="spin-reward-code">{rw.code}</b></div>
            <div className="flex items-center justify-center gap-1 text-gray-600" data-testid="spin-reward-expiry"><Clock size={13} /> Expires {fmtExpiry(rw.expires_at)}</div>
            {rw.terms && <div className="font-semibold text-[#111]" data-testid="spin-reward-terms">{rw.terms}</div>}
          </div>
        )}
        {rw ? <Button onClick={use} className="w-full mlobox-gradient text-white rounded-full" data-testid="spin-use-reward">Use my reward</Button>
          : <Button onClick={onClose} variant="outline" className="w-full rounded-full">Close</Button>}
      </DialogContent>
    </Dialog>
  );
}

function MyRewards({ rewards }) {
  const navigate = useNavigate();
  const { count } = useCart();
  if (!rewards.length) return null;
  return (
    <div className="w-full mt-6" data-testid="my-rewards">
      <div className="text-white font-semibold mb-2 flex items-center gap-2"><Gift size={16} className="text-[#FF8A3D]" /> My rewards</div>
      <div className="grid sm:grid-cols-2 gap-2">
        {rewards.map((r) => (
          <div key={r.code} className="bg-white/10 rounded-xl p-3 text-sm text-white" data-testid={`my-reward-${r.code}`}>
            <div className="flex justify-between gap-2"><b>{r.label}</b><span className={r.status === "unused" ? "text-emerald-400" : "text-white/50"}>{r.status}</span></div>
            <div className="text-white/70 text-xs mt-0.5">Code {r.code} · Expires {fmtExpiry(r.expires_at)}</div>
            {r.terms && <div className="text-[#FF8A3D] text-xs mt-0.5">{r.terms}</div>}
            {r.status === "unused" && <button className="text-xs font-semibold underline mt-1" data-testid={`my-reward-use-${r.code}`} onClick={() => { setPendingReward(r.code); if (count) navigate("/checkout"); else toast.info("Reward saved! Add a meal to your cart, then use it at checkout."); }}>Use at checkout</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SpinAndWin() {
  const [cfg, setCfg] = useState(null);
  const [session, setSession] = useState(loadSpinSession);
  const [me, setMe] = useState(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.get("/spin/config").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const loadMe = useCallback(async () => {
    if (!session) return;
    try { const r = await api.get("/spin/me", { headers: { "X-Spin-Token": session.token } }); setMe(r.data); }
    catch { clearSpinSession(); setSession(null); }
  }, [session]);
  useEffect(() => { loadMe(); }, [loadMe]);

  if (!cfg?.active) return null;

  const spin = async () => {
    setSpinning(true);
    try {
      const r = await api.post("/spin", {}, { headers: { "X-Spin-Token": session.token } });
      const seg = segmentsOf(cfg.prizes).find((s) => s.id === r.data.prize_id);
      const theta = seg ? seg.start + (seg.end - seg.start) * (0.2 + Math.random() * 0.6) : 0;
      setRotation((cur) => cur + 360 * 6 + ((360 - theta - (cur % 360)) + 720) % 360);
      setTimeout(() => {
        setSpinning(false);
        setResult(r.data);
        if (r.data.reward) confetti({ particleCount: 160, spread: 90, origin: { y: 0.6 }, colors: ["#FF6600", "#CC1A00", "#111111", "#FBF6EE"] });
        loadMe();
      }, 5700);
    } catch (e) { setSpinning(false); toast.error(err(e, "Could not spin")); loadMe(); }
  };

  const waitUntil = me?.next_spin_at;
  return (
    <section data-testid="spin-and-win" className="max-w-6xl mx-auto px-4 pt-6">
      <div className="bg-[#111111] rounded-3xl p-5 sm:p-8 relative overflow-hidden">
        <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full bg-[#FF6600]/20 blur-3xl" />
        <div className="relative grid md:grid-cols-2 gap-6 items-center">
          <div className="flex justify-center py-4"><SpinWheel prizes={cfg.prizes} rotation={rotation} spinning={spinning} /></div>
          <div className="flex flex-col items-start">
            <div className="text-[#FF8A3D] text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-1.5"><Sparkles size={12} /> Rewards</div>
            <h2 className="font-display text-white text-3xl sm:text-4xl font-extrabold mt-1">SPIN &amp; WIN</h2>
            <p className="text-white/70 text-sm mt-2 max-w-sm">Spin the MLOBOX wheel for discounts, free samosas, drinks and more. Rewards expire {cfg.expiry_days} days after you win. Free items: {cfg.free_item_note}.</p>
            <div className="mt-5 w-full">
              {!session ? <VerifyBox onVerified={setSession} /> : (
                <div className="space-y-2">
                  <div className="text-white/60 text-xs">Signed in as {session.email} · <button className="underline" onClick={() => { clearSpinSession(); setSession(null); setMe(null); }}>not you?</button></div>
                  <Button onClick={spin} disabled={spinning || !!waitUntil} data-testid="spin-now-btn"
                    className="mlobox-gradient text-white rounded-full px-10 py-6 text-lg font-extrabold shadow-[0_10px_30px_rgba(255,102,0,0.45)] hover:brightness-110">
                    {spinning ? "Spinning…" : "SPIN NOW"}
                  </Button>
                  {waitUntil && <div className="text-white/70 text-xs" data-testid="spin-next-at">Next spin available {fmtExpiry(waitUntil)}</div>}
                </div>
              )}
            </div>
            <MyRewards rewards={me?.rewards || []} />
          </div>
        </div>
      </div>
      <ResultDialog result={result} onClose={() => setResult(null)} />
    </section>
  );
}
